import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { LOGO_URL, BRAND_NAME } from "@/lib/api";

function formatErr(detail) {
  if (!detail) return "Sign-in failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/dashboard";

  const handle = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email & password required"); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(nextPath.startsWith("/") ? nextPath : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 relative overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(37,99,235,0.18) 0%, transparent 60%)" }}
      />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt={BRAND_NAME}
            className="h-28 w-28 object-contain mb-4"
            data-testid="login-logo"
          />
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">{BRAND_NAME}</h1>
          <p className="text-slate-400 text-sm mt-1">Property Management System</p>
        </div>

        {/* Form card */}
        <form
          onSubmit={handle}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 space-y-5"
          data-testid="login-form"
        >
          <div className="text-center pb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">Sign in</div>
            <div className="font-heading text-lg font-semibold">Staff access</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@ondina.com"
              className="mt-1 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
              data-testid="login-email"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Password</Label>
            <div className="relative mt-1">
              <Input
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-blue-500 pr-11"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                tabIndex={-1}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold"
            data-testid="login-submit"
          >
            {loading ? "Signing in..." : (<><LogIn size={16} /> Sign in</>)}
          </Button>

          <div className="text-[11px] text-slate-500 text-center pt-2 border-t border-white/5">
            Secure access · Shared across all your devices
          </div>
        </form>
      </div>
    </div>
  );
}
